import { buildAnalyticsPayload } from './analyticsPayload';
import { getRegionStateSync } from './region';
import { aswGetInstanceId } from '../platform/wix';

const queue = [];

/**
 * Cookie banner is a standalone BC script. Derive app origin + store hash from
 * its own src (`cookie-banner.js?instance=<storeHash>`), matching settings load.
 * Do not rely on main.js / initWix globals — they may not be present.
 */
function resolveFromScript() {
  try {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i -= 1) {
      const src = scripts[i].src || '';
      if (!src.includes('cookie-banner.js')) continue;
      const url = new URL(src);
      const instanceId = url.searchParams.get('instance') || null;
      return {
        proxy: `${url.origin}/api/v1`,
        instanceId,
      };
    }
  } catch (_) {}
  return { proxy: null, instanceId: null };
}

function proxyHandle() {
  if (typeof window !== 'undefined' && window.accessProProxyHandle) {
    return window.accessProProxyHandle;
  }
  return resolveFromScript().proxy;
}

function resolveInstanceId() {
  return aswGetInstanceId() || resolveFromScript().instanceId || null;
}

function postAnalytics(event, consentDetail = null) {
  const proxy = proxyHandle();
  if (!proxy) return false;

  // Ensure later queue flushes / other modules can reuse the same base URL.
  try {
    if (typeof window !== 'undefined' && !window.accessProProxyHandle) {
      window.accessProProxyHandle = proxy;
    }
  } catch (_) {}

  const region = getRegionStateSync();
  const url = `${proxy}/theme/cookie-banner/analytics`;
  const payload = buildAnalyticsPayload(event, consentDetail, {
    country: region && region.countryIso ? region.countryIso : undefined,
    device: region && region.device ? region.device : undefined,
    instanceId: resolveInstanceId(),
  });
  const body = JSON.stringify(payload);

  // Use a string (text/plain) like batch-analytics — Blob+application/json can
  // fail silently on cross-origin sendBeacon due to CORS.
  try {
    if (navigator && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon(url, body)) return true;
    }
  } catch (_) {}

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    mode: 'cors',
  }).catch(() => {});

  return true;
}

function flushQueue() {
  while (queue.length) {
    const item = queue.shift();
    if (!postAnalytics(item.event, item.consentDetail)) {
      queue.unshift(item);
      break;
    }
  }
}

/** Send cookie-banner analytics; queues briefly if proxy handle is not set yet. */
export function sendCookieBannerAnalytics(event, consentDetail = null) {
  try {
    if (postAnalytics(event, consentDetail)) return;
    queue.push({ event, consentDetail });
    // Retry shortly in case script/globals finish initializing.
    setTimeout(flushQueue, 250);
    setTimeout(flushQueue, 1000);
  } catch (_) {}
}

/** Call after proxy handle is assigned (e.g. from liquid or ping). */
export function flushCookieBannerAnalytics() {
  flushQueue();
}
